import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { useOfferMode } from "@nju/data-access-portal-offers";
import { pushSimpleEvent } from "@nju/data-layer";
import { SimSetDigits } from "@nju/dev-tip";
import { getFeatureFlagValue } from "@nju/feature-flags";
import { ErrorMessage, Input } from "@nju/form";
import { portalRoutes } from "@nju/routes";
import { A, Button, Panel, Separator, Text } from "@nju/ui";
import { GeneralError } from "../partials";
import { ISimCardFlow } from "../types";
import { mobileSimCardMachine } from "./mobileSimCard.machine";
import { WrongSimCode } from "./partials";

interface IMobileSimCardPageProps {
  flow: ISimCardFlow;
}

export function MobileSimCardPage({ flow }: IMobileSimCardPageProps) {
  const router = useRouter();
  const mode = useOfferMode();
  const memberOrchestrator = useMemberOrchestrator();
  const isDevTipFlagEnabled = getFeatureFlagValue("devTip") === "1";

  const redirectURL =
    flow === "ONBOARDING"
      ? portalRoutes.mojeKonto.index.get({
          mode,
          ...(memberOrchestrator?.mobile?.userId && {
            userId: memberOrchestrator?.mobile?.userId,
          }),
        })
      : portalRoutes.mojeKonto.mojaKarta.index.get({
          ...(memberOrchestrator?.mobile?.userId && {
            userId: memberOrchestrator?.mobile?.userId,
          }),
        });

  const [current, send] = useMachine(mobileSimCardMachine, {
    actions: {
      setSimSetSuccess: (_, event) => {
        if (event.type === "SIM_SET_SUCCESS") {
          router.push(redirectURL);
        }
      },
    },
    context: {
      flow,
      orchestratorId:
        flow === "ONBOARDING"
          ? memberOrchestrator?.mobile?.id
          : memberOrchestrator?.changeSimEsim?.id,
    },
  });

  const { context, matches } = current;
  const { sim, simError } = context;

  React.useEffect(() => {
    if (simError) {
      pushSimpleEvent({
        event: "error_add_sim_card",
        eventName: "NotNumberShort",
      });
    }
  }, [simError]);

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      ({ target }) => {
        const { value } = target;
        send({
          type: "SET_SIM",
          value,
        });
      },
      [send],
    );

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        send({ type: "SUBMIT" });
      },
      [send],
    );

  if (matches("wrongSimCode")) {
    return <WrongSimCode userId={memberOrchestrator?.mobile?.userId} />;
  }

  if (matches("generalError")) {
    return <GeneralError />;
  }

  return (
    <Panel>
      <Text
        size={{
          "@onlyMobile": "xl",
          "@fromTablet": "keyS",
        }}
        weight="bold"
      >
        dodaj kartę SIM
      </Text>
      <Separator size="xs" tone="invisible" />
      <Text
        size={{
          "@onlyMobile": "m",
          "@fromTablet": "l",
        }}
      >
        wpisz 4 ostatnie cyfry numeru karty SIM
      </Text>
      <Separator size="s" tone="invisible" />
      <form onSubmit={handleSubmit}>
        <div className="from-tablet:max-w-[400px]">
          <Input
            label="4 ostatnie cyfry numeru karty SIM"
            name="sim-card"
            onChange={onInputChange}
            value={sim}
          />
        </div>
        <Separator size="xxs" tone="invisible" />
        {simError && <ErrorMessage message={simError} />}
        <Separator size="xs" tone="invisible" />
        <Text
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "l",
          }}
        >
          Masz przed sobą swoją nową kartę SIM? Teraz połączymy ją z aplikacją.
          Wpisz 4 ostatnie cyfry numeru karty (znajdziesz go pod kodem
          kreskowym).
        </Text>
        <Separator size="xs" tone="invisible" />
        <div className="flex justify-between only-mobile:gap-3 only-mobile:flex-col-reverse flex-row">
          <Link href={redirectURL} passHref={true}>
            <A
              size={{
                "@onlyMobile": "s",
              }}
              variant="secondary"
            >
              anuluj
            </A>
          </Link>
          <Button
            id="button_nm_dodaj_karte_sim"
            isLoading={!matches("ready")}
            size={{ "@onlyMobile": "s" }}
            type="submit"
          >
            dodaj kartę SIM
          </Button>
        </div>
        {isDevTipFlagEnabled && (
          <SimSetDigits
            orderId={
              flow === "ONBOARDING"
                ? memberOrchestrator?.mobile?.orderId
                : memberOrchestrator?.changeSimEsim?.orderId
            }
          />
        )}
      </form>
    </Panel>
  );
}
